import * as React from "react";
import {
  Create,
  TextInput,
  DateTimeInput,
  FormDataConsumer,
  ArrayInput,
  SimpleFormIterator,
  useSimpleFormIteratorItem,
  ReferenceInput,
  AutocompleteInput,
  SelectInput,
  DateInput,
} from "react-admin";
import { Box, Typography, Divider } from "@mui/material";
import { SimpleForm } from "../common/SimpleForm";
import {
  validateRequired,
  validateProductEventName,
  validateProductEventDates,
  validateProductEventEndDate,
} from "../utils/Validations";
import Spacer from "../common/Spacer";
import { RESTAURANT_TYPES } from "../enums/RestaurantTypes";
import { ENTITY_AVAILABILITY_STATUS } from "../enums/EntityAvailabilityStatus";
import { useWatch } from "react-hook-form";
import AbsoluteSource from "../common/AbsoluteSource";

const transform = (data) => {
  const { restaurantProduct, products, ...rest } = data;
  return {
    ...rest,
    to: rest.to ?? rest.from,
    products: products?.filter(Boolean) || [],
  };
};

const ProductEventProductRow = () => {
  const { index } = useSimpleFormIteratorItem();
  const restaurantProduct = useWatch({ name: "restaurantProduct" });
  return (
    <Box sx={{ flex: "1" }}>
      <Box sx={{ display: "flex" }}>
        <AbsoluteSource>
          <ReferenceInput
            source={`restaurantProduct[${index}]`}
            reference="restaurants"
            perPage={100}
            filter={{
              active: true,
              type: RESTAURANT_TYPES.REGULAR,
            }}
            fullWidth
          >
            <AutocompleteInput
              filterToQuery={(searchText) => ({
                name: searchText,
              })}
              label="Restaurante"
              optionText="name"
              fullWidth
            />
          </ReferenceInput>
        </AbsoluteSource>

        <Spacer />

        <ReferenceInput
          // Empty source = the array's scalar element (products.<i>)
          source=""
          reference="products"
          perPage={10000}
          sort={{ field: "name", order: "ASC" }}
          filter={{
            restaurant: restaurantProduct?.[index],
            status: [ENTITY_AVAILABILITY_STATUS.ACTIVE],
          }}
          fullWidth
        >
          <SelectInput
            optionText="name"
            label="Producto"
            readOnly={!restaurantProduct?.[index]}
            fullWidth
          />
        </ReferenceInput>
      </Box>
    </Box>
  );
};

const ProductEventCreate = () => (
  <Create
    title="Crear Evento"
    mutationMode="pessimistic"
    redirect="list"
    transform={transform}
  >
    <SimpleForm>
      <FormDataConsumer>
        {({ formData }) => (
          <>
            <TextInput
              source="name"
              label="Nombre"
              validate={validateProductEventName}
              fullWidth
            />

            <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
              <DateTimeInput
                source="fromPresale"
                label="Preventa desde"
                parse={(v) => (v ? new Date(v).toISOString() : null)}
                validate={validateRequired}
                fullWidth
              />
              <DateInput
                source="from"
                label="Evento desde"
                parse={(v) =>
                  v ? new Date(`${v}T03:00:00Z`).toISOString() : null
                }
                validate={[validateRequired, validateProductEventDates]}
                fullWidth
              />
              <DateInput
                source="to"
                label="Evento hasta"
                parse={(v) =>
                  v ? new Date(`${v}T03:00:00Z`).toISOString() : null
                }
                validate={[validateRequired, validateProductEventEndDate]}
                fullWidth
              />
            </Box>

            <Box sx={{ width: "100%", my: 3 }}>
              <Divider />
            </Box>

            <Typography variant="h6" gutterBottom>
              Productos del Evento
            </Typography>

            <ArrayInput source="products" label="">
              <SimpleFormIterator disableReordering>
                <ProductEventProductRow />
              </SimpleFormIterator>
            </ArrayInput>
          </>
        )}
      </FormDataConsumer>
    </SimpleForm>
  </Create>
);

export default ProductEventCreate;
